import React, { useCallback, useEffect, useState } from "react";
import { faBars, faBuildingColumns, faChalkboardUser, faChevronDown, faChurch, faCircle, faGear, faGlobe, faGrip, faPersonChalkboard, faTableColumns, faUserGroup } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { Authorize } from "./Authorize";
import { Button, ConfigProvider, Drawer, Dropdown, Menu, MenuProps } from "antd";
import Head from "next/head";
import Logo from "./Logo";
import { useAtomValue } from "jotai";
import { permissionsAtom } from "@/stores/permissionAtom";
import AuthPermissionListener from "./AuthPermissionListener";
import logout from "@/functions/logout";
import { reportMenuAtom } from "@/stores/reportMenuAtom";
import ReportMenuListener from "./ReportMenuListener";

const PRIMARY_COLOR = "#175CFF";
const WHITE_COLOR = "#FFFFFF";
const BLACK_COLOR = "#3C424F";

const DefaultLayout: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const router = useRouter();
    const { data: session, status } = useSession();

    const handleLogout = () => {
        if (status === 'authenticated') {
            logout();
        }
    }

    const permissionAtom = useAtomValue(permissionsAtom);
    const reportMenusAtom = useAtomValue(reportMenuAtom);

    const displayUserName = session?.user?.name;
    const displayUserEmail = session?.user?.email;

    const isPermissionsLoading = status === "authenticated" && permissionAtom === null;
    const permissionKeys = permissionAtom || [];
    const isUserFirstAccess = !permissionKeys.length;

    const [selected, setSelected] = useState([router.pathname]);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    const getMenu = useCallback((): MenuProps["items"] => {
        const menu: MenuProps["items"] = [];

        // If loading or no perms, return empty
        if (isPermissionsLoading || isUserFirstAccess) return menu;

        const keorganisasianPermissionList = [
            "akses_sidebar_pengurus",
            "akses_sidebar_mpk_mpkw",
            "akses_sidebar_organisasi_komisi",
            "akses_sidebar_task_force",
            // "akses_sidebar_program_kerja",
        ];

        const hasKeorganisasianAccess = keorganisasianPermissionList.some(p =>
            permissionKeys.includes(p)
        );

        const laporanPermissionList = [
            "akses_manajemen_laporan",
            "akses_sidebar_laporan",
        ];

        const hasLaporanAccess = laporanPermissionList.some(p =>
            permissionKeys.includes(p)
        );

        // const manajemenSekolahPermissionList = [
        //     "akses_sidebar_manajemen_lowongan_pekerjaan",
        //     "akses_sidebar_manajemen_pelamar_pekerjaan",
        // ];

        // const hasManajemenSekolahAccess = manajemenSekolahPermissionList.some(p =>
        //     permissionKeys.includes(p)
        // );

        menu.push(
            {
                key: "/dashboard",
                label: "Dashboard",
                icon: <FontAwesomeIcon icon={faTableColumns} />,
                onClick: () => router.push("/dashboard"),
            },
            ...(permissionKeys.includes("akses_sidebar_yayasan") ? [{
                key: "/yayasan",
                label: "Yayasan",
                icon: <FontAwesomeIcon icon={faBuildingColumns} />,
                onClick: () => router.push("/yayasan"),
            }] : []),
            ...(permissionKeys.includes("akses_sidebar_sekolah") ? [{
                key: "/sekolah",
                label: "Sekolah",
                icon: <FontAwesomeIcon icon={faChurch} />,
                onClick: () => router.push("/sekolah"),
            }] : []),
            ...(permissionKeys.includes("akses_sidebar_gtk") ? [{
                key: "/guru-dan-tenaga-kependidikan",
                label: "Guru & Tenaga Kependidikan (GTK)",
                icon: <FontAwesomeIcon icon={faChalkboardUser} />,
                onClick: () => router.push("/guru-dan-tenaga-kependidikan"),
            }] : []),
            ...(permissionKeys.includes("akses_sidebar_mitra") ? [{
                key: "/mitra",
                label: "Mitra",
                icon: <FontAwesomeIcon icon={faGlobe} />,
                onClick: () => router.push("/mitra"),
            }] : []),
            ...(permissionKeys.includes("akses_sidebar_trainer") ? [{
                key: "/trainer",
                label: "Trainer",
                icon: <FontAwesomeIcon icon={faPersonChalkboard} />,
                onClick: () => router.push("/trainer"),
            }] : []),
            ...(hasKeorganisasianAccess ? [
                {
                    key: "/keorganisasian",
                    label: "Keorganisasian",
                    icon: <FontAwesomeIcon icon={faGrip} />,
                    children: [
                        ...(permissionKeys.includes("akses_sidebar_pengurus") ? [
                            {
                                key: "/keorganisasian/pengurus",
                                label: "Pengurus",
                                icon: <FontAwesomeIcon icon={faCircle}
                                    style={{
                                        fontSize: "0.2rem",
                                        verticalAlign: "middle",
                                        color: router.pathname === "/keorganisasian/pengurus" ? WHITE_COLOR : PRIMARY_COLOR,
                                    }}
                                />,
                                onClick: () => router.push("/keorganisasian/pengurus"),
                            }
                        ] : []),
                        ...(permissionKeys.includes("akses_sidebar_mpk_mpkw") ? [
                            {
                                key: "/keorganisasian/mpk-mpkw",
                                label: "MPK & MPKW",
                                icon: <FontAwesomeIcon icon={faCircle}
                                    style={{
                                        fontSize: "0.2rem",
                                        verticalAlign: "middle",
                                        color: router.pathname === "/keorganisasian/mpk-mpkw" ? WHITE_COLOR : PRIMARY_COLOR,
                                    }}
                                />,
                                onClick: () => router.push("/keorganisasian/mpk-mpkw"),
                            }
                        ] : []),
                        ...(permissionKeys.includes("akses_sidebar_organisasi_komisi") ? [
                            {
                                key: "/keorganisasian/struktur-organisasi-dan-komisi",
                                label: (
                                    <span
                                        style={{
                                            color: (router.pathname === "/keorganisasian/struktur-organisasi" || router.pathname === "/keorganisasian/komisi") ? PRIMARY_COLOR : BLACK_COLOR,
                                            padding: "14px 0px"
                                        }}
                                    >
                                        Struktur Organisasi & <br /> Komisi
                                    </span>
                                ),
                                icon: <FontAwesomeIcon icon={faCircle}
                                    style={{
                                        fontSize: "0.2rem",
                                        verticalAlign: "middle",
                                        color: router.pathname === "/keorganisasian/struktur-organisasi-dan-komisi" ? WHITE_COLOR : PRIMARY_COLOR,
                                    }}
                                />,
                                children: [
                                    {
                                        key: "/keorganisasian/struktur-organisasi",
                                        label: "Struktur Organisasi",
                                        icon: <FontAwesomeIcon icon={faCircle}
                                            style={{
                                                fontSize: "0.2rem",
                                                verticalAlign: "middle",
                                                color: router.pathname === "/keorganisasian/struktur-organisasi" ? WHITE_COLOR : PRIMARY_COLOR,
                                            }}
                                        />,
                                        onClick: () => router.push("/keorganisasian/struktur-organisasi"),
                                    },
                                    {
                                        key: "/keorganisasian/komisi",
                                        label: "Komisi",
                                        icon: <FontAwesomeIcon icon={faCircle}
                                            style={{
                                                fontSize: "0.2rem",
                                                verticalAlign: "middle",
                                                color: router.pathname === "/keorganisasian/komisi" ? WHITE_COLOR : PRIMARY_COLOR,
                                            }}
                                        />,
                                        onClick: () => router.push("/keorganisasian/komisi"),
                                    },
                                ]
                            }
                        ] : []),
                        ...(permissionKeys.includes("akses_sidebar_task_force") ? [
                            {
                                key: "/keorganisasian/task-force",
                                label: "Task Force",
                                icon: <FontAwesomeIcon icon={faCircle}
                                    style={{
                                        fontSize: "0.2rem",
                                        verticalAlign: "middle",
                                        color: router.pathname === "/keorganisasian/task-force" ? WHITE_COLOR : PRIMARY_COLOR,
                                    }}
                                />,
                                onClick: () => router.push("/keorganisasian/task-force"),
                            }
                        ] : []),
                        // ...(permissionKeys.includes("akses_sidebar_program_kerja") ? [
                        //     {
                        //         key: "/keorganisasian/program-kerja",
                        //         label: "Program Kerja",
                        //         icon: <FontAwesomeIcon icon={faCircle}
                        //             style={{
                        //                 fontSize: "0.2rem",
                        //                 verticalAlign: "middle" ,
                        //                 color: router.pathname === "/keorganisasian/program-kerja" ? WHITE_COLOR : PRIMARY_COLOR,
                        //             }}
                        //         />,
                        //         onClick: () => router.push("/keorganisasian/program-kerja"),
                        //     }
                        // ] : []),
                    ],
                },
            ] : []),
            ...(hasLaporanAccess ? [{
                key: "/laporan",
                label: "Laporan",
                icon: <FontAwesomeIcon icon={faTableColumns} />,
                children: [
                    ...(permissionKeys.includes("akses_manajemen_laporan") ? [{
                        key: "/laporan/manajemen-laporan",
                        label: "Manajemen Laporan",
                        icon: <FontAwesomeIcon icon={faCircle}
                                    style={{
                                        fontSize: "0.2rem",
                                        verticalAlign: "middle",
                                        color: router.pathname === "/laporan/manajemen-laporan" ? WHITE_COLOR : PRIMARY_COLOR,
                                    }}
                                />,
                        onClick: () => router.push("/laporan/manajemen-laporan"),
                    }] : []),
                    ...(permissionKeys.includes("akses_sidebar_laporan") && reportMenusAtom && reportMenusAtom.length > 0 ? reportMenusAtom.map((menuItem) => {
                        return {
                            key: `/laporan/lihat/${menuItem.id}`,
                            label: `${menuItem.name}`,
                            icon: <FontAwesomeIcon icon={faCircle}
                                    style={{
                                        fontSize: "0.2rem",
                                        verticalAlign: "middle",
                                        color: router.asPath === `/laporan/lihat/${menuItem.id}` ? WHITE_COLOR : PRIMARY_COLOR,
                                    }}
                                />,
                            onClick: () => router.push(`/laporan/lihat/${menuItem.id}`),
                        };
                    }) : []),
                ]
            }] : []),
            ...(permissionKeys.includes("akses_sidebar_manajemen_role") ? [{
                key: "/manajemen-role",
                label: "Manajemen Role",
                icon: <FontAwesomeIcon icon={faUserGroup} />,
                onClick: () => router.push("/manajemen-role"),
            }] : []),
            ...(permissionKeys.includes("akses_sidebar_pengaturan") ? [{
                key: "/pengaturan",
                label: "Pengaturan",
                icon: <FontAwesomeIcon icon={faGear} />,
                onClick: () => router.push("/pengaturan"),
            }] : []),

            // ...(hasManajemenSekolahAccess ? [
            //     {
            //         key: "/manajemen-sekolah",
            //         label: "Manajemen Sekolah",
            //         icon: <FontAwesomeIcon icon={faGrip} />,
            //         children: [
            //             ...(permissionKeys.includes("akses_sidebar_manajemen_lowongan_pekerjaan") ? [
            //                 {
            //                     key: "/manajemen-sekolah/lowongan-pekerjaan",
            //                     label: "Lowongan Pekerjaan",
            //                     icon: <FontAwesomeIcon icon={faCircle}
            //                         style={{
            //                             fontSize: "0.2rem",
            //                             verticalAlign: "middle",
            //                             color: router.pathname === "/manajemen-sekolah/lowongan-pekerjaan" ? WHITE_COLOR : PRIMARY_COLOR,
            //                         }}
            //                     />,
            //                     onClick: () => router.push("/manajemen-sekolah/lowongan-pekerjaan"),
            //                 }
            //             ] : []),
            //             ...(permissionKeys.includes("akses_sidebar_manajemen_pelamar_pekerjaan") ? [
            //                 {
            //                     key: "/manajemen-sekolah/pelamar-pekerjaan",
            //                     label: "Pelamar Pekerjaan",
            //                     icon: <FontAwesomeIcon icon={faCircle}
            //                         style={{
            //                             fontSize: "0.2rem",
            //                             verticalAlign: "middle",
            //                             color: router.pathname === "/manajemen-sekolah/pelamar-pekerjaan" ? WHITE_COLOR : PRIMARY_COLOR,
            //                         }}
            //                     />,
            //                     onClick: () => router.push("/manajemen-sekolah/pelamar-pekerjaan"),
            //                 }
            //             ] : []),
            //         ],
            //     },
            // ] : []),
        );
        return menu;
    }, [router, session, permissionKeys, isPermissionsLoading, isUserFirstAccess])

    const userMenuItems = [
        {
            key: "logout",
            label: "Log Out",
            danger: true,
            onClick: () => handleLogout(),
            className: "logout-menu"
        },
    ];

    const menuItems = getMenu();
    const isOnlyDashboard = menuItems?.length === 1;

    useEffect(() => {
        const menuItems = getMenu();

        const currentPath = router.pathname;

        const findMatchingKey = (items: MenuProps["items"], path: string): { selectedKey: string | null, parentKey: string | null } => {
            let matched: { selectedKey: string | null, parentKey: string | null } = { selectedKey: null, parentKey: null };

            for (const item of items ?? []) {
                if (!item) continue;

                if ("children" in item && item.children) {
                    for (const child of item.children) {
                        if (!child) continue;
                        if (path.startsWith(child.key as string)) {
                            return { selectedKey: child.key as string, parentKey: item.key as string };
                        }
                    }
                } else if (path.startsWith(item.key as string)) {
                    matched = { selectedKey: item.key as string, parentKey: null };
                }
            }

            return matched;
        };

        const { selectedKey, parentKey } = findMatchingKey(menuItems, currentPath);

        if (selectedKey) {
            setSelected([selectedKey]);

            if (parentKey) {
                setOpenKeys([parentKey]);
            } else {
                setOpenKeys([]);
            }
        }

    }, [router.pathname, getMenu]);

    const onOpenChange = (keys: string[]) => {
        setOpenKeys(keys);
    };



    return (
        <ConfigProvider
            theme={{
                components: {
                    Menu: {
                        colorItemBg: WHITE_COLOR,
                        colorItemBgSelected: PRIMARY_COLOR,
                        colorItemText: BLACK_COLOR,
                        colorItemTextSelected: WHITE_COLOR,
                        colorSplit: "transparent",
                        radiusItem: 4,
                        colorItemTextHover: PRIMARY_COLOR,
                    },
                },
            }}
        >
            <div className="min-h-screen flex bg-light-grey">
                <AuthPermissionListener />
                <ReportMenuListener />
                <Head>
                    <meta key="meta-charset" charSet="utf-8" />
                    <meta
                        key="meta-viewport"
                        name="viewport"
                        content="width=device-width, initial-scale=1"
                    />
                </Head>

                {isPermissionsLoading ? (
                    <aside className="hidden lg:flex w-sidebar max-w-[255px] bg-white my-5 ml-6 p-10 items-center justify-center rounded-lg shadow-own">
                        <span>Loading Menu...</span>
                    </aside>
                ) : (!isUserFirstAccess && !isOnlyDashboard && (
                    <>
                        <aside
                            className="hidden lg:block w-sidebar max-w-[255px] bg-white my-5 ml-6 p-10 pb-3 px-1 rounded-lg shadow-own">
                            <div className="mb-6 flex justify-center">
                                <Logo />
                            </div>
                            <Menu
                                mode="inline"
                                items={getMenu()}
                                selectedKeys={selected}
                                openKeys={openKeys}
                                onOpenChange={onOpenChange}
                                onSelect={(e) => setSelected(e.selectedKeys)}
                                className="menu-wrap-text menu-timeline"
                            />
                        </aside>

                        <Drawer
                            placement="left"
                            open={drawerOpen}
                            onClose={() => setDrawerOpen(false)}
                        >
                            <Menu
                                mode="inline"
                                items={getMenu()}
                                selectedKeys={selected}
                                openKeys={openKeys}
                                onOpenChange={onOpenChange}
                                onSelect={(e) => setSelected(e.selectedKeys)}
                                className="menu-wrap-text menu-timeline"
                            />
                        </Drawer>
                    </>
                ))}

                <div className="flex-1 flex flex-col w-full overflow-x-auto !bg-[#F4F5F7]" >
                    <div className="lg:hidden bg-topbar grid grid-cols-3 px-8 py-4 items-center">
                        <Button onClick={() => setDrawerOpen(true)} type="primary">
                            <FontAwesomeIcon icon={faBars} />
                        </Button>
                        <div className="flex justify-center">
                            <Logo />
                        </div>
                        <div></div>
                    </div>

                    <header className="hidden lg:flex h-16 bg-white mt-5 mx-5 rounded-lg shadow-md">
                        <div className="flex justify-end items-center w-full pr-5">
                            <div className="flex items-center gap-2">
                                <div className="flex flex-col">
                                    <span className="font-bold">
                                        {displayUserName}
                                    </span>
                                    <span className="text-sm">{displayUserEmail}</span>
                                </div>
                                <Dropdown
                                    menu={{ items: userMenuItems }}
                                    placement="bottomLeft"
                                    trigger={["click"]}
                                >
                                    <Button
                                        type="text"
                                        icon={<FontAwesomeIcon icon={faChevronDown} />}
                                    />
                                </Dropdown>
                            </div>
                        </div>
                    </header>

                    <main className="flex-1 m-5 bg-light-grey rounded-lg">
                        {children}
                    </main>
                </div>
            </div>
        </ConfigProvider>
    );
}

export const WithDefaultLayout = (page: React.ReactElement) => (
    <Authorize>
        <DefaultLayout>
            {page}
        </DefaultLayout>
    </Authorize>
);
